import classNames from 'classnames';
import { get, groupBy } from 'lodash';
import capitalize from 'lodash/capitalize';
import { useCallback, useMemo } from 'react';
import { isIbanCountry } from '@/modules/common/utils/bank-details.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { useVerifyEntityPaymentInfoMutation } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import InfoDetailsSet from '@/modules/issuer-dashboard/components/InfoDetailsSet.tsx';
import ShareholderContactDetails from '@/modules/issuer-dashboard/components/ShareholderContactDetails.tsx';
import { dividendReinvestmentOptions } from '@/modules/issuer-dashboard/components/modals/IssuerAddSubscriptionModal.tsx';
import { Badge, Button, InfoCard } from 'ui';
import { AlertBlueIcon } from 'ui/icons';
import { EntitiesPaymentInfoStatus, ShareholderExtendResponse } from 'api';

interface DetailsTabProps {
  shareholder?: ShareholderExtendResponse | null;
}

const DetailsTab = ({ shareholder }: DetailsTabProps) => {
  const payment_info_status = shareholder?.entity?.payment_info_status;
  const isPaymentInfoVerified = payment_info_status === EntitiesPaymentInfoStatus.Verified;

  const {
    mutateAsync: sendPaymentVerificationRequest,
    isLoading: iSendingPaymentVerificationLoading,
  } = useVerifyEntityPaymentInfoMutation();

  const handleSendPaymentVerificationRequest = useCallback(() => {
    sendPaymentVerificationRequest({
      investorName: shareholder?.entity?.entity_name || '',
      entity_id: shareholder?.entity_id || 0,
    });
  }, [sendPaymentVerificationRequest, shareholder?.entity?.entity_name, shareholder?.entity_id]);

  const bankDetailsData = useMemo(() => {
    const getBankValue = (value?: string | null) =>
      value && value.toLowerCase() !== 'unknown' ? value : 'Not provided';
    const country = shareholder?.entity_details?.investor_details?.country;
    const countrySpecificDetails =
      country === 'AUS'
        ? [{ label: 'BSB', value: getBankValue(shareholder?.entity?.bank_account_bsb) }]
        : [
            ...(country === 'USA'
              ? [
                  {
                    label: 'Routing number',
                    value: getBankValue(shareholder?.entity?.bank_account_routing_number),
                  },
                ]
              : []),
            ...(isIbanCountry(country)
              ? [{ label: 'IBAN', value: getBankValue(shareholder?.entity?.bank_account_iban) }]
              : []),
            { label: 'SWIFT/BIC', value: getBankValue(shareholder?.entity?.bank_account_swift) },
          ];

    const base = [
      { label: 'Bank name', value: getBankValue(shareholder?.entity?.bank_name) },
      ...countrySpecificDetails,
      { label: 'Account number', value: getBankValue(shareholder?.entity?.bank_account_number) },
      { label: 'Account name', value: getBankValue(shareholder?.entity?.bank_account_name) },
    ];

    const reinvestments =
      shareholder?.dividend_reinvestment_extended?.map(item => {
        const option = dividendReinvestmentOptions.find(opt => opt.value === item.is_reinvest);
        return {
          label: (
            <span>
              Distribution election - <span className="font-bold">{item.register_class_name}</span>
            </span>
          ),
          value: option?.label ?? '',
        };
      }) ?? [];

    return [...base, ...reinvestments];
  }, [shareholder]);

  const authorizedPeopleData = useMemo(() => {
    const groups = groupBy(shareholder?.entity_details?.associated_people, 'role');

    return Object.entries(groups).flatMap(([role, people]) =>
      people.map(({ name }, index) => ({
        label: capitalize(`${role} #${index + 1}`),
        value: name,
      })),
    );
  }, [shareholder?.entity_details?.associated_people]);

  const taxDetailsData = useMemo(() => {
    const taxResidenceCountries = (
      get(shareholder, 'entity_details.investor_details.tax_residence', []) || []
    ).map((residence, index) => ({
      label: `Country of Tax Residence #${index + 1}`,
      value:
        countryOptions.find(({ value }) => residence.country === value)?.label || residence.country,
    }));

    return [
      {
        label: 'Account Holder’s GIIN',
        value: shareholder?.entity_details?.tax?.account_holders_gin,
      },
      {
        label: 'Sponsoring Entity’s Name',
        value: shareholder?.entity_details?.tax?.sponsoring_entity_name,
      },
      ...taxResidenceCountries,
      {
        label:
          'Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or resident alien of the U.S. even if residing outside the U.S.',
        value: shareholder?.entity_details?.tax?.is_us_person ? 'Yes' : 'No',
      },
      {
        label: 'Non U.S Person Certification',
        value: shareholder?.entity_details?.tax?.non_us_person_certification,
      },
      {
        label: 'Is the entity an Investment Entity managed by another Financial Institution?',
        value: shareholder?.entity_details?.tax?.is_managed_by_another_institution ? 'Yes' : 'No',
      },
      { label: 'CRS status', value: shareholder?.entity_details?.tax?.crs_status },
    ];
  }, [shareholder]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="flex flex-col gap-6">
        <ShareholderContactDetails shareholder={shareholder} />
        <InfoCard
          title={
            <div className="flex gap-4">
              <span>Bank details</span>
              <Badge
                className={classNames('text-white text-xs py-0.5 !h-6', {
                  'bg-blue-700': !isPaymentInfoVerified,
                  'bg-green-600': isPaymentInfoVerified,
                  none: false,
                })}
              >
                {!isPaymentInfoVerified && 'Pending'}
                {isPaymentInfoVerified && 'Verified'}
              </Badge>
            </div>
          }
        >
          {isPaymentInfoVerified && (
            <div className="flex gap-2 justify-between items-center p-3.5 bg-blue-700/[.08] mb-6 rounded">
              <div className="flex gap-3 w-fit max-w-72 lg:max-w-96">
                <img src={AlertBlueIcon} alt="" width={24} height={24} />
                <span className="text-sm ">
                  Ask investor to confirm their bank information is correct or update it
                </span>
              </div>

              <Button
                variant="secondary"
                className="h-10 shrink-0 min-w-[150px] bg-white"
                onClick={handleSendPaymentVerificationRequest}
                loading={iSendingPaymentVerificationLoading}
              >
                Verify bank info
              </Button>
            </div>
          )}

          <InfoDetailsSet set={bankDetailsData} />
        </InfoCard>
      </div>
      <div className="flex flex-col gap-6">
        {!!authorizedPeopleData.length && (
          <InfoCard title="Authorised People">
            <InfoDetailsSet set={authorizedPeopleData} />
          </InfoCard>
        )}

        <InfoCard title="Tax details">
          <InfoDetailsSet set={taxDetailsData} />
        </InfoCard>
      </div>
    </div>
  );
};

export default DetailsTab;
